import { Route, Routes } from '@angular/router';

import { SUPPORTED_LOCALES, type SupportedLocale } from '../i18n/locales';
import { authGuard } from '../shell/auth/auth-guard';
import { roleGuard } from '../shell/auth/role-guard';
import { NotFoundPage } from './pages/not-found/not-found-page';
import { PlaceholderPage } from './pages/placeholder-page/placeholder-page';
import { canLeaveStorageLocationStructure } from './pages/storage-locations/storage-location-structure/storage-location-structure.guard';
import { canLeaveStorageLocations } from './pages/storage-locations/storage-locations.guard';

function createLocaleRoute(locale: SupportedLocale): Route {
  const imaPath = `/${locale}/ima`;

  return {
    path: locale,
    // Blocks the entire locale subtree for an unauthenticated user — CanMatch
    // runs before route matching completes, so nothing under it renders.
    canMatch: [authGuard],
    data: { locale },
    children: [
      {
        path: '',
        redirectTo: 'ima',
        pathMatch: 'full',
      },
      {
        path: 'testing',
        loadComponent: async () => {
          const { AngularButton } = await import('./testing/angular-button/angular-button');

          return AngularButton;
        },
        children: [
          {
            path: '',
            loadComponent: async () => {
              const { HelloWorld } = await import('./testing/hello-world/hello-world');

              return HelloWorld;
            },
            data: {
              storeRoute: `${imaPath}/store`,
            },
          },
          {
            path: 'store',
            loadComponent: async () => {
              const { Store } = await import('./testing/store/store');

              return Store;
            },
            data: {
              backRoute: imaPath,
            },
          },
          {
            path: 'profile',
            loadComponent: async () => {
              const { Profile } = await import('./testing/profile/profile');

              return Profile;
            },
            data: {
              storeRoute: `${imaPath}/store`,
            },
          },
        ],
      },
      {
        path: 'ima',
        children: [
          {
            path: '',
            loadComponent: async () => {
              const { HelloWorld } = await import('./testing/hello-world/hello-world');

              return HelloWorld;
            },
            data: {
              storeRoute: `${imaPath}/store`,
            },
          },
          {
            path: 'store',
            loadComponent: async () => {
              const { Store } = await import('./testing/store/store');

              return Store;
            },
            data: {
              backRoute: imaPath,
            },
          },
        ],
      },

      // Main sidebar destinations (see src/app/layout/main-nav and
      // src/app/layout/nav-items.ts, which ModuleHeader reads its title
      // from). Placeholders only — IMA-1408 covers the persistent
      // layout/navigation, not these pages. Paths must stay in sync with
      // src/app/app.routes.ts.
      { path: 'dashboard', component: PlaceholderPage },
      { path: 'demands', component: PlaceholderPage },
      { path: 'control-center', component: PlaceholderPage },
      {
        path: 'storage-locations',
        loadComponent: async () => {
          const { StorageLocationsPage } =
            await import('./pages/storage-locations/storage-locations');

          return StorageLocationsPage;
        },
        canDeactivate: [canLeaveStorageLocations],
      },
      {
        path: 'storage-locations/edit-structure',
        canDeactivate: [canLeaveStorageLocationStructure],
        loadComponent: async () => {
          const { StorageLocationStructurePage } =
            await import('./pages/storage-locations/storage-location-structure/storage-location-structure-page');

          return StorageLocationStructurePage;
        },
      },
      {
        path: 'item-catalog',
        loadComponent: async () => {
          const { ItemCatalogPage } = await import('./pages/item-catalog/item-catalog');

          return ItemCatalogPage;
        },
      },
      { path: 'help', component: PlaceholderPage },
      // Demonstrates roleGuard on top of authGuard (already applied to the
      // whole locale subtree above). "Administrator" is the real IMA realm
      // role (see ima-backend/ima-keycloak/realms/IMA-realm.json).
      { path: 'settings', component: PlaceholderPage, canMatch: [roleGuard(['Administrator'])] },
      {
        // Exclusive route: standalone Angular input/output pattern examples,
        // rendered alone (not nested under AngularButton) so none of
        // AngularButton's Shell/MFE communication demo UI renders alongside it.
        path: 'testing/angular-inputs',
        loadComponent: async () => {
          const { AngularInputs } = await import('./testing/angular-inputs/angular-inputs');

          return AngularInputs;
        },
      },
      {
        path: 'testing/storage-locations',
        loadComponent: async () => {
          const { StorageLocations } =
            await import('./testing/storage-locations/storage-locations');

          return StorageLocations;
        },
      },

      // Renders under the locale prefix (keeps AppRoot's header/nav/footer
      // chrome) for any unmatched path below a supported locale — must stay last.
      { path: '**', component: NotFoundPage },
    ],
  };
}

export const standaloneRoutes: Routes = [
  {
    path: '',
    redirectTo: '/en/ima',
    pathMatch: 'full',
  },

  ...SUPPORTED_LOCALES.map(createLocaleRoute),

  // Catches paths with no (or an unrecognized) locale prefix, e.g. "/xyz".
  { path: '**', component: NotFoundPage },
];
